import type { FC } from 'react';
import React from 'react';
import cn from 'classnames';
import type { FormApi } from 'final-form';
import { Grid } from '@alfa-bank/site-grid-universal';
import { Aligner } from '@alfa-bank/site-aligner';
import { DcPhoneInput } from '@alfa-bank/site-dc-phone-input';
import type { TFormValues } from '@alfa-bank/site-form/src/models';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import { useDispatch, useSelector } from 'react-redux';
import type { IFirstStepData } from '@alfa-bank/site-dc-form-api/src/models';
import {
    changePhoneNumberRequest,
    selectFirstStepData,
    setStep,
} from '@alfa-bank/site-dc-form-api/store';

import { preparePhoneValue } from '../../utils/helpers';
import { changePhoneNumberStepAnalytics as analytics } from '../../analytics';

import type { TChangePhoneNumber } from './models';
import { AnalyticsSpy } from './analytics-spy';

export const ChangePhoneNumber: FC<TChangePhoneNumber> = ({
    Button,
    Form,
    Layout,
    Text,
    styles,
}) => {
    const dispatch = useDispatch();
    const firstStep = useSelector(selectFirstStepData);

    const onSubmit = (formRef: HTMLFormElement, form: FormApi<TFormValues>) => {
        const { changedPhoneNumber } = form.getState().values as unknown as {
            changedPhoneNumber: IFirstStepData['mobilePhone'];
        };

        const formattedPhone = preparePhoneValue(changedPhoneNumber.toString());

        dispatch(changePhoneNumberRequest(formattedPhone));

        sendEvent(analytics.submit);
    };

    const onCancel = () => {
        dispatch(setStep('smsConfirm'));

        sendEvent(analytics.cancel);
    };

    return (
        <Layout
            classNames={{
                layoutContent: cn(styles.changePhoneNumber, styles.external),
            }}
            contentWidth={752}
            hasPadding={false}
            scroll={false}
            styler={{ margin: 0 }}
        >
            <Form
                className={cn(styles.formContent, styles.external)}
                dataTestId="change-number-screen"
                onSubmit={onSubmit}
                showFormOuterWrapper={false}
            >
                <Aligner align="center">
                    <Text styler={{ marginBottom: 24, marginTop: 0 }} view="small">
                        Введите новый номер
                    </Text>
                </Aligner>

                <DcPhoneInput
                    initialValue={
                        firstStep?.mobilePhone ? `+7${firstStep?.mobilePhone}` : undefined
                    }
                    name="changedPhoneNumber"
                    settings={{ dataTestId: 'phone-change', noBrackets: true }}
                    styler={{ marginBottom: 24 }}
                />
                <Grid className={cn(styles.external, styles.buttonGap)} collapse justify="between">
                    <Button
                        block
                        dataTestId="button-cancel"
                        onClick={onCancel}
                        size="xs"
                        type="button"
                        view="secondary"
                    >
                        Отменить
                    </Button>
                    <Button block dataTestId="button-change" size="xs" type="submit" view="primary">
                        Изменить
                    </Button>
                </Grid>
                <AnalyticsSpy />
            </Form>
        </Layout>
    );
};
